import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v3";
import type { ModalComponentProps } from "@/lib/modal";
import {
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCloseTicket } from "@/lib/api";

const closeTicketSchema = z.object({
  reason: z
    .string()
    .max(1000, "Reason must be 1000 characters or less")
    .optional(),
});

type CloseTicketFormValues = z.infer<typeof closeTicketSchema>;

export interface CloseTicketModalProps {
  applicationGuildId: string;
  ticketId: number;
}

export function CloseTicketModal({
  applicationGuildId,
  ticketId,
  onClose,
}: CloseTicketModalProps & ModalComponentProps) {
  const closeTicket = useCloseTicket(applicationGuildId);

  const form = useForm<CloseTicketFormValues>({
    resolver: zodResolver(closeTicketSchema),
    defaultValues: { reason: "" },
  });

  const onSubmit = async (values: CloseTicketFormValues) => {
    await closeTicket.mutateAsync({
      ticketId,
      reason: values.reason?.trim() || undefined,
    });
    onClose();
  };

  return (
    <>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>Close Ticket</ResponsiveModalTitle>
        <ResponsiveModalDescription>
          Are you sure you want to close this ticket? You can optionally provide
          a reason.
        </ResponsiveModalDescription>
      </ResponsiveModalHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Close Reason (optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Reason for closing this ticket..."
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <ResponsiveModalFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={closeTicket.isPending}
            >
              {closeTicket.isPending ? "Closing..." : "Close Ticket"}
            </Button>
          </ResponsiveModalFooter>
        </form>
      </Form>
    </>
  );
}

CloseTicketModal.contentClassName = "sm:max-w-md";
CloseTicketModal.displayName = "CloseTicketModal";
